import LoginIcon from "@mui/icons-material/Login";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import {
  Avatar,
  Box,
  Dropdown,
  ListDivider,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
} from "@mui/joy";
import { useColorScheme } from "@mui/joy/styles";
import * as motion from "motion/react-client";
import { useContext, useEffect, useState } from "react";
import {
  AiFillMoon,
  AiFillSun,
  AiOutlineLogout,
  AiOutlineProfile,
} from "react-icons/ai";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import blackLogo from '../assets/fintrack-high-resolution-logo-transparent.svg'
import whiteLogo from '../assets/Untitled (2).svg'

function ThemeToggle({ checked, onChange, mode }) {
  const containerStyle = {
    width: 56,
    height: 30,
    backgroundColor:
      mode === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.03)",
    borderRadius: 99,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    padding: 3,
    boxSizing: "border-box",
    border:
      mode === "light"
        ? "1px solid rgba(0,0,0,0.04)"
        : "1px solid rgba(255,255,255,0.06)",
  };

  const handleStyle = {
    width: 24,
    height: 24,
    backgroundColor: mode === "light" ? "inherit" : "#111",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow:
      mode === "light"
        ? "0 1px 2px rgba(0,0,0,0.08)"
        : "0 1px 2px rgba(0,0,0,0.6)",
  };

  return (
    <button
      type="button"
      aria-pressed={checked}
      aria-label={checked ? "Switch to light mode" : "Switch to dark mode"}
      onClick={() => onChange(!checked)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
      }}
    >
      <div
        style={{
          ...containerStyle,
          justifyContent: checked ? "flex-end" : "flex-start",
          transition: "background-color 160ms ease, border 160ms ease",
        }}
      >
        <motion.div
          layout
          transition={{
            type: "spring",
            damping: 18,
            stiffness: 260,
            duration: 0.2,
          }}
          style={handleStyle}
        >
          {checked ? (
            <AiFillMoon size={14} />
          ) : (
            <AiFillSun size={14} color="#111" />
          )}
        </motion.div>
      </div>
    </button>
  );
}

function Navbar({ drawerIsOpen }) {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const { mode, setMode } = useColorScheme();

  const [checked, setChecked] = useState(() => {
    const saved = localStorage.getItem("theme");
    if (saved) return saved === "dark";
    return mode === "dark";
  });

  // Apply a smooth transition to the page root so background & color changes animate
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const appRoot = document.getElementById("root");

    // Save previous transitions to restore on unmount
    const prevRootTransition = root.style.transition;
    const prevBodyTransition = body.style.transition;
    const prevAppRootTransition = appRoot?.style?.transition;

    const transitionValue =
      "background-color 300ms ease, color 300ms ease, border-color 300ms ease, box-shadow 300ms ease";

    root.style.transition = transitionValue;
    body.style.transition = transitionValue;
    if (appRoot) appRoot.style.transition = transitionValue;

    // clean up (restore previous inline values) when component unmounts
    return () => {
      root.style.transition = prevRootTransition;
      body.style.transition = prevBodyTransition;
      if (appRoot) appRoot.style.transition = prevAppRootTransition || "";
    };
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const initial = saved || mode || "dark";

    setMode(initial);
    setChecked(initial === "dark");

    document.documentElement.style.setProperty(
      "--background-color",
      initial === "light" ? "#f2f2f2" : "hsl(0, 0%, 1%)",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const newMode = checked ? "dark" : "light";
    setMode(newMode);
    localStorage.setItem("theme", newMode);
    // update the CSS variable (if you're using it)
    document.documentElement.style.setProperty(
      "--background-color",
      newMode === "light" ? "#f2f2f2" : "hsl(0, 0%, 1%)",
    );
  }, [checked, setMode]);

  const handleThemeToggle = (nextChecked) => {
    setChecked(Boolean(nextChecked));
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // theme-aware colors for the pill
  const isLight = mode === "light";
  const borderColor = isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.12)";
  const labelColor = isLight ? "#111" : "#fff";
  const hoverBg = isLight ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.03)";

  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        left: 0,
        right: 0,
        width: "100vw",
        marginLeft: "calc(-50vw + 10%)",
        marginRight: "calc(-50vw + 10%)",
        zIndex: drawerIsOpen ? 1 : 9999,
        backdropFilter: "blur(6px)",
        mt: "-0.5%",
        borderBottom:
          mode === "light"
            ? "0.2px solid hsla(240, 7.70%, 2.50%, 0.15)"
            : "0.2px solid rgba(229, 231, 235, 0.14)",
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Logo */}
    <Link to="/" style={{ textDecoration: "none" }}>
  <img
    src={mode === "light" ? blackLogo: whiteLogo}
    alt="FinTrack logo"
    style={{
      height: "32px",          // controls size
      width: "auto",           // keeps aspect ratio
      maxWidth: "100%",
      objectFit: "contain",
      display: "block",
    }}
  />
</Link>


        {/* Right Side Actions */}
        {user ? (
          <>
            {/* Theme Toggle with Labels */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AiFillSun
                  style={{
                    opacity: checked ? 0.4 : 1,
                    color: mode === "light" ? "#111" : undefined,
                  }}
                />

                <ThemeToggle
                  checked={checked}
                  onChange={handleThemeToggle}
                  mode={mode}
                />

                <AiFillMoon
                  style={{
                    opacity: checked ? 1 : 0.4,
                    color: mode === "light" ? "#111" : undefined,
                  }}
                />
              </Box>
            </Box>

            {/* User Dropdown */}
            <Dropdown>
              <MenuButton size="sm" variant="plain" color="transparent">
                <Avatar
                  src={user?.profile_picture}
                  sx={{
                    cursor: "pointer",
                    transition: "transform 0.2s ease",
                    "&:hover": {
                      transform: "scale(1.05)",
                    },
                  }}
                >
                  {user?.name?.[0]}
                </Avatar>
              </MenuButton>
              <Menu size="sm" sx={{ zIndex: 99999 }} placement="bottom-end">
                <MenuItem disabled>
                  <ListItemDecorator >
                    <AiOutlineProfile style={{color: mode==="light"  ? "#454545":null}}/>
                  </ListItemDecorator>
                  Profile
                </MenuItem>

                <ListDivider />
                <MenuItem onClick={handleLogout} color="danger">
                  <ListItemDecorator>
                    <AiOutlineLogout style={{color: mode==="light"  ? "#454545":null}}/>
                  </ListItemDecorator>
                  Logout
                </MenuItem>
              </Menu>
            </Dropdown>
          </>
        ) : (
          <>
            {/* Theme Toggle for Non-Logged Users */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" }, // hides on xs screens
                    opacity: checked ? 0.4 : 1,
                    color: mode === "light" ? "#111" : undefined,
                  }}
                >
                  <AiFillSun />
                </Box>

                <ThemeToggle
                  checked={checked}
                  onChange={handleThemeToggle}
                  mode={mode}
                />

                <Box
                  sx={{
                    display: { xs: "none", sm: "block" }, // hide on xs
                    opacity: checked ? 1 : 0.4,
                    color: mode === "light" ? "#111" : undefined,
                  }}
                >
                  <AiFillMoon />
                </Box>
              </Box>
            </Box>

            {/* Combined Auth Pill */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                borderRadius: "999px",
                overflow: "hidden",
                border: `1px solid ${borderColor}`,
                background: "transparent",
                ml: 1.5,
                minHeight: 36,
              }}
            >
              {/* Login segment */}
              <Box
                component={Link}
                to="/login"
                aria-label="Login"
                sx={{
    px: { xs: 0, sm: 1.25 }, // 0 on xs, 1.25 on sm and up
                  py: 0.5,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
    minWidth: { xs: 60, sm: 88 }, // 60px on xs screens, 88px on sm+
                  justifyContent: "center",
                  textDecoration: "none",
                  "&:hover": { backgroundColor: hoverBg },
                }}
              >
                <LoginIcon sx={{ color: labelColor }} />
                <Typography
                  level="body2"
                  sx={{
                    fontFamily: "monospace",
                    color: labelColor,
                    display: { xs: "none", sm: "block" }, // hide on xs
                  }}
                >
                  Login
                </Typography>
              </Box>

              {/* vertical divider */}
              <Box sx={{ width: 1, height: 28, bgcolor: borderColor }} />

              {/* Sign Up segment */}
              <Box
                component={Link}
                to="/register"
                aria-label="Sign Up"
                sx={{
    px: { xs: 0, sm: 1.25 }, // 0 on xs, 1.25 on sm and up
                  py: 0.5,
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
    minWidth: { xs: 60, sm: 88 }, // 60px on xs screens, 88px on sm+
                  justifyContent: "center",
                  textDecoration: "none",
                  "&:hover": { backgroundColor: hoverBg },
                }}
              >
                <PersonAddAltOutlinedIcon sx={{ color: labelColor }} />
                <Typography
                  level="body2"
                  sx={{
                    fontFamily: "monospace",
                    color: labelColor,
                    display: { xs: "none", sm: "block" }, // hide on xs
                  }}
                >
                  Sign Up
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

export default Navbar;
